import { supabase } from '../lib/supabase';

/** One MOT/annual test as DVSA returns it. */
export interface MotTest {
  registrationAtTimeOfTest?: string | null;
  completedDate?: string | null;
  testResult?: string | null;
  expiryDate?: string | null;
  /** A string in DVSA's schema. Parse only where a number is genuinely needed. */
  odometerValue?: string | null;
  odometerUnit?: string | null;
  odometerResultType?: string | null;
  motTestNumber?: string | null;
  /** 'DVSA' | 'DVA NI' | 'CVS'. Northern Irish records carry no defects. */
  dataSource?: string | null;
  location?: string | null;
  defects?: Array<{ text?: string | null; type?: string | null; dangerous?: boolean | null }> | null;
}

/** The MOT picture for a vehicle, already reduced from the raw DVSA response. */
export interface MotHistory {
  /** Current certificate expiry, from the newest PASSED test that has one. */
  expiryDate: string | null;
  /** Set instead of expiryDate for a vehicle too new to have been tested. */
  firstDueDate: string | null;
  lastTestDate: string | null;
  lastTestResult: string | null;
  lastOdometerValue: string | null;
  lastOdometerUnit: string | null;
  advisoryCount: number;
  /**
   * 'Yes' | 'No' | 'Unknown' | 'Unavailable'.
   *
   * 'Unknown' means DVSA holds no recall data for this vehicle — it is NOT a
   * statement that the vehicle has no outstanding recall, and must never be
   * rendered as one. That would be a safety claim we have no basis for.
   */
  hasOutstandingRecall: string | null;
  registrationDate: string | null;
  manufactureDate: string | null;
  firstUsedDate: string | null;
  tests: MotTest[];
}

export interface VehicleDetails {
  make: string | null;
  yearOfManufacture: string | null;
  engineCapacity: number | null;
  fuelType: string | null;
  colour: string | null;
  motStatus: string | null;
  motExpiryDate: string | null;
  taxStatus: string | null;
  taxDueDate: string | null;

  /**
   * From DVSA, not DVLA — the Vehicle Enquiry Service has never returned a
   * model, which is why this was a required field the user typed by hand on
   * every single vehicle. Null when DVSA is unavailable or has no record.
   */
  model?: string | null;
  /** Null when DVSA could not be reached; see `sources.mot` for why. */
  motHistory?: MotHistory | null;
  /** Which providers answered. Lets the UI be specific about what is missing. */
  sources?: { dvla: string; mot: string };
}

/** Why a lookup failed. Callers need this to say something true to the user. */
export type VrmFailureKind =
  /** Doesn't look like a UK registration — rejected before we call DVLA. */
  | 'invalid_format'
  /** DVLA has no vehicle with that registration (404). */
  | 'not_found'
  /** DVLA rate-limited us (429). */
  | 'rate_limited'
  /** Anything else: DVLA down, misconfigured, network, auth. */
  | 'unavailable';

export class VrmLookupError extends Error {
  readonly kind: VrmFailureKind;

  constructor(kind: VrmFailureKind, message: string) {
    super(message);
    this.name = 'VrmLookupError';
    this.kind = kind;
  }
}

/**
 * Permissive UK registration check.
 *
 * Deliberately loose: it rejects obvious junk (no digits, no letters, wrong
 * length) but doesn't try to enumerate every plate format. Britain has current,
 * prefix, suffix, dateless, Northern Irish and personalised plates, and wrongly
 * refusing a real one is far worse than making one wasted API call.
 */
export function looksLikeUkVrm(vrm: string): boolean {
  const clean = vrm.replace(/\s/g, '').toUpperCase();
  if (clean.length < 2 || clean.length > 8) return false;
  if (!/^[A-Z0-9]+$/.test(clean)) return false;
  return /[A-Z]/.test(clean) && /[0-9]/.test(clean);
}

/**
 * Look up a vehicle's details via the secure DVLA proxy edge function.
 * 
 * @param vrm - The Vehicle Registration Mark (license plate)
 * @returns Strongly typed minimal vehicle details
 * @throws Error with a user-friendly message if the lookup fails
 */
export async function lookupVrm(vrm: string): Promise<VehicleDetails> {
  const { data, error } = await supabase.functions.invoke<{ error?: string } & Partial<VehicleDetails>>('vrm-lookup', {
    body: { vrm },
  });

  // Supabase edge functions returning non-2xx status set the 'error' object.
  // The specific JSON message we built (e.g., { "error": "Vehicle not found" }) can be extracted.
  if (error) {
    // The HTTP status is what separates "this reg doesn't exist" from "the
    // service is broken". Callers previously showed the same message for both,
    // which told a user with a typo that our systems were down.
    const status: number | undefined =
      error.context && typeof error.context.status === 'number' ? error.context.status : undefined;

    let errorMessage = 'Failed to retrieve vehicle data';
    try {
      // The error context usually contains the Response if it was a FunctionsHttpError
      if (error.context && typeof error.context.json === 'function') {
        const errorBody = await error.context.json();
        if (errorBody && errorBody.error) {
          errorMessage = errorBody.error;
        }
      } else if (error.message) {
         // Some Supabase client versions put the JSON directly in the message string
         try {
           const parsed = JSON.parse(error.message);
           if (parsed.error) errorMessage = parsed.error;
         } catch {
           errorMessage = error.message;
         }
      }
    } catch {
      // Fallback
    }

    const kind: VrmFailureKind =
      status === 404 ? 'not_found'
      : status === 429 ? 'rate_limited'
      // The proxy returns 400 both for a malformed VRM and for a DVLA response
      // it couldn't use; the message distinguishes them.
      : status === 400 && /vrm/i.test(errorMessage) ? 'invalid_format'
      : 'unavailable';

    // A reg DVLA has never heard of, or one that fails our format check, is a
    // normal outcome of a lookup — not a fault. Logging those at error level
    // filled the console with red for a working service and buried the faults
    // that do need attention.
    if (kind === 'unavailable' || kind === 'rate_limited') {
      console.error('VRM lookup failed (%s):', kind, errorMessage, error);
    }

    throw new VrmLookupError(kind, errorMessage);
  }

  // Fallback check if the edge function returned 200/2xx but explicitly embedded an error string
  if (data?.error) {
    throw new VrmLookupError('unavailable', data.error);
  }

  // At this point we are confident the data matches our VehicleDetails interface
  return data as VehicleDetails;
}

/**
 * Persist a vehicle's MOT history against a vehicle that already exists.
 *
 * Deliberately fire-and-forget at the call sites: the vehicle and the customer
 * are already saved by the time this runs, so a DVSA outage must not surface as
 * a failure of something that actually succeeded. The worst case is a vehicle
 * with no stored MOT history and a "Refresh MOT" button that fixes it.
 */
export async function refreshMotHistory(vehicleId: string): Promise<boolean> {
  const { error } = await supabase.functions.invoke('mot-history-refresh', {
    body: { vehicle_id: vehicleId },
  });
  if (error) {
    console.warn('MOT history refresh failed for vehicle', vehicleId, error);
    return false;
  }
  return true;
}

/**
 * The same refresh, but bounded, for the moment right after a vehicle is
 * created and the user is about to look at it.
 *
 * Fire-and-forget is right when nobody is waiting. It is wrong here: creating a
 * customer or a job lands the user on the record a beat later, and the MOT
 * write had not finished, so the vehicle showed no MOT date, no advisories and
 * no mileage until they found the Refresh button. Waiting a bounded moment
 * turns that into "it was just there".
 *
 * The cap is what keeps it safe — a slow or down DVSA delays nothing beyond it,
 * and the refresh still completes in the background afterwards.
 */
export async function refreshMotHistoryWithin(
  vehicleId: string,
  timeoutMs = 4000,
): Promise<boolean> {
  const inFlight = refreshMotHistory(vehicleId);
  const timedOut = new Promise<boolean>((resolve) =>
    setTimeout(() => resolve(false), timeoutMs));
  return await Promise.race([inFlight, timedOut]);
}
